import { useCallback, useEffect, useRef } from "react";
import { invoiceService } from "../services/invoice.service";
import type { InvoiceResponseDTO } from "../types/invoice.types";

const POLL_INTERVAL_MS = 3000;
const MAX_ATTEMPTS = 5;

/**
 * Polling leve para acompanhar a evolução `WAITING → ISSUED` de uma NF
 * recém-emitida (a autorização chega de forma assíncrona via webhook).
 *
 * Reconsulta `GET /admin/invoices/{id}` a cada 3s, no máximo 5 vezes, e
 * propaga cada resultado via `onUpdate`. Para assim que o status sai de
 * `WAITING` ou as tentativas se esgotam. É best-effort: erros de rede são
 * silenciados e os timers são limpos no unmount.
 */
export function useInvoiceStatusPolling(
  onUpdate: (invoice: InvoiceResponseDTO) => void,
) {
  const timerRef = useRef<number | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    return () => {
      cancelledRef.current = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const poll = useCallback(
    (id: number) => {
      const run = (attemptsLeft: number) => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = window.setTimeout(async () => {
          try {
            const invoice = await invoiceService.getById(id);
            if (cancelledRef.current) return;
            onUpdate(invoice);
            if (invoice.status === "WAITING" && attemptsLeft > 1) {
              run(attemptsLeft - 1);
            }
          } catch {
            // Polling é best-effort — o admin pode usar "Atualizar" manualmente.
          }
        }, POLL_INTERVAL_MS);
      };
      run(MAX_ATTEMPTS);
    },
    [onUpdate],
  );

  return poll;
}
